import { darken, getContrastRatio } from '@mui/material/styles';

/** Apariencia por empresa: se guarda en Configuraciones del tenant (usuario global ''). */
export const APARIENCIA_SECCION = 'APARIENCIA';
export const APARIENCIA_CAMPO_COLOR = 'COLOR_ACENTO';
export const APARIENCIA_CAMPO_ETIQUETA = 'ETIQUETA';
export const APARIENCIA_CAMPO_AVISO = 'MOSTRAR_AVISO';

export const APARIENCIA_ETIQUETA_MAX = 20;

export interface TenantAppearance {
  colorAcento: string | null;
  etiqueta: string | null;
  mostrarAviso: boolean;
}

export const EMPTY_TENANT_APPEARANCE: TenantAppearance = {
  colorAcento: null,
  etiqueta: null,
  mostrarAviso: false,
};

export interface AppearancePreset {
  id: string;
  label: string;
  description: string;
  appearance: TenantAppearance;
}

export const APPEARANCE_PRESETS: AppearancePreset[] = [
  {
    id: 'principal',
    label: 'Principal',
    description: 'Colores estándar, sin etiqueta ni aviso.',
    appearance: EMPTY_TENANT_APPEARANCE,
  },
  {
    id: 'secundario',
    label: 'Secundario',
    description: 'Ámbar, etiqueta SECUNDARIO y franja de aviso.',
    appearance: { colorAcento: '#d97706', etiqueta: 'SECUNDARIO', mostrarAviso: true },
  },
];

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

export function normalizeHexColor(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  return HEX_COLOR_RE.test(withHash) ? withHash.toLowerCase() : null;
}

export function normalizeEtiqueta(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  return raw ? raw.slice(0, APARIENCIA_ETIQUETA_MAX) : null;
}

export function normalizeAppearance(raw: Partial<TenantAppearance> | null | undefined): TenantAppearance {
  return {
    colorAcento: normalizeHexColor(raw?.colorAcento),
    etiqueta: normalizeEtiqueta(raw?.etiqueta),
    mostrarAviso: Boolean(raw?.mostrarAviso),
  };
}

export function isAppearanceEmpty(appearance: TenantAppearance): boolean {
  return !appearance.colorAcento && !appearance.etiqueta && !appearance.mostrarAviso;
}

export function sameAppearance(a: TenantAppearance, b: TenantAppearance): boolean {
  return a.colorAcento === b.colorAcento && a.etiqueta === b.etiqueta && a.mostrarAviso === b.mostrarAviso;
}

/** Texto legible (blanco o casi negro) sobre el color dado, según luminancia WCAG. */
export function contrastTextColor(hex: string): '#ffffff' | '#111827' {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return '#111827';
  const channel = (start: number) => {
    const c = parseInt(normalized.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.179 ? '#111827' : '#ffffff';
}

const MIN_INK_CONTRAST = 3;

/**
 * Variante del color usable como texto/borde sobre fondo blanco.
 * Colores claros (blanco, amarillos, pasteles) se oscurecen hasta ser legibles.
 */
export function accentInk(color: string): string {
  const normalized = normalizeHexColor(color);
  if (!normalized) return color;
  let ink = normalized;
  for (let step = 1; step <= 10 && getContrastRatio(ink, '#ffffff') < MIN_INK_CONTRAST; step += 1) {
    ink = darken(normalized, step * 0.1);
  }
  return ink;
}

const CACHE_PREFIX = 'tenantAppearance:';

export function appearanceCacheKey(tenantId: number): string {
  return `${CACHE_PREFIX}${tenantId}`;
}

export function parseCachedAppearance(raw: string | null): TenantAppearance | null {
  if (!raw) return null;
  try {
    return normalizeAppearance(JSON.parse(raw) as Partial<TenantAppearance>);
  } catch {
    return null;
  }
}

export function readCachedAppearance(tenantId: number): TenantAppearance | null {
  try {
    return parseCachedAppearance(localStorage.getItem(appearanceCacheKey(tenantId)));
  } catch {
    return null;
  }
}

export function writeCachedAppearance(tenantId: number, appearance: TenantAppearance): void {
  try {
    if (isAppearanceEmpty(appearance)) {
      localStorage.removeItem(appearanceCacheKey(tenantId));
    } else {
      localStorage.setItem(appearanceCacheKey(tenantId), JSON.stringify(appearance));
    }
  } catch {
    // localStorage no disponible (modo privado / cuota): la apariencia queda solo en memoria.
  }
}

/** Colores configurados conocidos en este navegador para el set de empresas del usuario. */
export function readCachedAccentMap(tenantIds: number[]): Record<number, string> {
  const map: Record<number, string> = {};
  tenantIds.forEach((id) => {
    const color = readCachedAppearance(id)?.colorAcento;
    if (color) map[id] = color;
  });
  return map;
}

export function tenantIdFromCacheKey(key: string | null): number | null {
  if (!key || !key.startsWith(CACHE_PREFIX)) return null;
  const id = Number(key.slice(CACHE_PREFIX.length));
  return Number.isFinite(id) ? id : null;
}
